import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { Button, FormHelperText, IconButton, InputLabel, OutlinedInput, Stack, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { useTheme } from '@mui/material/styles';
import { ArrowBack, AutoFixHighOutlined, Close, EditOutlined } from '@mui/icons-material';
import { PersonaCreateOption } from './PersonaCreateOption';
import { useModalContext } from '../../modal/ModalContext';
import {
	descriptionList,
	genderList,
	getPersonaTemplateBlocks,
	jobList,
	locationList,
	quoteList,
	statusList,
} from './PersonaCreateTemplate';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const createPersonaMutation = graphql(`
	mutation organization_createPersona(
		$name: String!
		$age: Int!
		$quote: String!
		$description: String!
		$status: PersonaStatus!
		$gender: PersonaGender!
		$job: String!
		$location: String!
		$blocks: [PersonaCreateBlocksEntityRelationInput!]
	) {
		createPersona(
			data: {
				name: $name
				age: $age
				quote: $quote
				description: $description
				status: $status
				gender: $gender
				job: $job
				location: $location
				blocks: $blocks
			}
		) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.personas.error.empty-name' })),
	});

export const createPersonaModalSchema = z.object({});

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

type Props = z.infer<typeof createPersonaModalSchema>;

const PersonaCreate: FC<Props> = () => {
	const intl = useIntl();
	const theme = useTheme();
	const [selectedOption, setSelectedOption] = useState<string>('');
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { handleSubmit, register, formState, reset } = useTypedForm({
		schema: createFormSchema(intl),
	});

	const cancelDialog = () => {
		setSelectedOption('');
		closeModal();
	};

	// sync form initial values
	useEffect(() => {
		reset({
			name: '',
		});
	}, [reset]);

	const { mutate: createPersona } = useApiMutation(createPersonaMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.createPersona.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'persona.create.error' }),
				},
				errorMessage: result.createPersona.errorMessage,
				openSnackbar,
			});
		},
	});

	const onSubmit = (values: FormValues) => {
		setSelectedOption('');
		createPersona(
			{
				age: Math.floor(Math.random() * (55 - 18 + 1)) + 18,
				quote: quoteList[Math.floor(Math.random() * quoteList.length)],
				description: descriptionList[Math.floor(Math.random() * descriptionList.length)],
				status: statusList[Math.floor(Math.random() * statusList.length)],
				gender: genderList[Math.floor(Math.random() * genderList.length)],
				job: jobList[Math.floor(Math.random() * jobList.length)],
				location: locationList[Math.floor(Math.random() * locationList.length)],
				blocks: getPersonaTemplateBlocks(),
				...values,
			},
			{ onSuccess: cancelDialog },
		);
	};

	return (
		<form noValidate onSubmit={handleSubmit(onSubmit)}>
			<Stack direction={'column'} justifyContent={'center'} padding={3} gap={4}>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<IconButton onClick={cancelDialog}>
						<ArrowBack htmlColor={theme.palette.grey[700]} />
					</IconButton>
					<IconButton onClick={cancelDialog}>
						<Close htmlColor={theme.palette.grey[700]} />
					</IconButton>
				</Stack>
				<Stack direction={'column'} gap={0.5} textAlign={'center'}>
					<Typography variant={'h1'} fontSize={30} fontWeight={700}>
						<FormattedMessage id={'form.persona.title.create'} />
					</Typography>
					<Typography variant={'body1'} color={theme.palette.grey[500]}>
						<FormattedMessage id={'form.persona.title.description'} />
					</Typography>
				</Stack>
				<Stack direction={'row'} justifyContent={'center'}>
					<Stack maxWidth={'450px'} width={'100%'} gap={2}>
						<PersonaCreateOption
							setSelectedOption={setSelectedOption}
							title={intl.formatMessage({ id: 'form.persona.create.option.1.title' })}
							desc={intl.formatMessage({ id: 'form.persona.create.option.1.desc' })}
							name={'manual'}
							icon={<EditOutlined />}
							selectedOption={selectedOption}
						/>
						<PersonaCreateOption
							setSelectedOption={setSelectedOption}
							title={intl.formatMessage({ id: 'form.persona.create.option.2.title' })}
							desc={intl.formatMessage({ id: 'form.persona.create.option.2.desc' })}
							name={'ai'}
							icon={<AutoFixHighOutlined htmlColor={theme.palette.primary[400]} />}
							iconBackground={theme.palette.primary.lighter}
							disabled
						/>
					</Stack>
				</Stack>
				<Stack direction={'row'} justifyContent={'center'}>
					<Stack maxWidth={'450px'} width={'100%'}>
						{selectedOption === 'manual' && (
							<>
								<InputLabel htmlFor={'name'}>
									<FormattedMessage id={'form.persona.label.name'} />
								</InputLabel>
								<OutlinedInput {...register('name')} id={'name'} />
								{formState.errors.name != null && <FormHelperText error>{formState.errors.name.message}</FormHelperText>}
							</>
						)}
					</Stack>
				</Stack>
				<Stack direction={'row'} justifyContent={'center'}>
					<Stack maxWidth={'450px'} width={'100%'}>
						{selectedOption ? (
							<Button
								type={'submit'}
								variant={'contained'}
								sx={{ backgroundColor: theme.palette.primary[400] }}
								fullWidth
								disabled={formState.isSubmitting}
							>
								<FormattedMessage id={'form.persona.create.create-button'} />
							</Button>
						) : null}
					</Stack>
				</Stack>
			</Stack>
		</form>
	);
};

export { PersonaCreate };
